import React, { useState, useRef } from 'react';

// ── Button ────────────────────────────────────────────────────
export function Button({ children, variant = 'primary', size = '', loading, className = '', ...props }) {
  return (
    <button
      className={`btn btn-${variant} ${size ? `btn-${size}` : ''} ${className}`}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading && <span className="spinner" style={{ width: 14, height: 14, borderWidth: 1.5 }} />}
      {children}
    </button>
  );
}

// ── Badge ─────────────────────────────────────────────────────
export function Badge({ children, color = 'muted' }) {
  return <span className={`badge badge-${color}`}>{children}</span>;
}

// ── Card ──────────────────────────────────────────────────────
export function Card({ children, className = '', style }) {
  return <div className={`card ${className}`} style={style}>{children}</div>;
}

// ── Modal ─────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, maxWidth = 600 }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Input ─────────────────────────────────────────────────────
export function Input({ label, hint, error, className = '', ...props }) {
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <input className={`form-input ${error ? 'border-accent' : ''} ${className}`} {...props} />
      {hint && !error && <span className="form-hint">{hint}</span>}
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

// ── Textarea ──────────────────────────────────────────────────
export function Textarea({ label, hint, error, rows = 4, ...props }) {
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <textarea className={`form-input form-textarea ${error ? 'border-accent' : ''}`} rows={rows} {...props} />
      {hint && !error && <span className="form-hint">{hint}</span>}
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

// ── Select ────────────────────────────────────────────────────
export function Select({ label, hint, error, options = [], placeholder, ...props }) {
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <select className={`form-input form-select ${error ? 'border-accent' : ''}`} {...props}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(opt =>
          typeof opt === 'string'
            ? <option key={opt} value={opt}>{opt}</option>
            : <option key={opt.value} value={opt.value}>{opt.label}</option>
        )}
      </select>
      {hint && !error && <span className="form-hint">{hint}</span>}
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

// ── Tags Input ────────────────────────────────────────────────
export function TagsInput({ label, tags = [], onChange, suggestions = [], placeholder = 'Type and press Enter' }) {
  const [input, setInput] = useState('');
  const [showSug, setShowSug] = useState(false);
  const inputRef = useRef();

  const filtered = suggestions.filter(s => s.toLowerCase().includes(input.toLowerCase()) && !tags.includes(s));

  const addTag = (tag) => {
    const t = tag.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setInput('');
    setShowSug(false);
    inputRef.current?.focus();
  };

  const removeTag = (tag) => onChange(tags.filter(t => t !== tag));

  const handleKey = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className="form-group" style={{ position: 'relative' }}>
      {label && <label className="form-label">{label}</label>}
      <div className="tags-container" onClick={() => inputRef.current?.focus()}>
        {tags.map(tag => (
          <span key={tag} className="tag-chip">
            {tag}
            <button type="button" onClick={() => removeTag(tag)}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="tags-input"
          value={input}
          onChange={e => { setInput(e.target.value); setShowSug(true); }}
          onKeyDown={handleKey}
          onBlur={() => setTimeout(() => setShowSug(false), 200)}
          placeholder={tags.length === 0 ? placeholder : ''}
        />
      </div>
      {showSug && input && filtered.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 100, top: '100%', left: 0, right: 0,
          background: 'var(--canvas-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-md)',
          maxHeight: 200, overflowY: 'auto', marginTop: 2
        }}>
          {filtered.slice(0, 8).map(s => (
            <div
              key={s}
              style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13 }}
              onMouseDown={() => addTag(s)}
              className="suggestion-item"
            >{s}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Checkbox Group ────────────────────────────────────────────
export function CheckboxGroup({ label, options, selected = [], onChange }) {
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {options.map(opt => (
          <label key={opt} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
            borderRadius: 'var(--radius-sm)', border: '1.5px solid',
            borderColor: selected.includes(opt) ? 'var(--ink)' : 'var(--border)',
            background: selected.includes(opt) ? 'var(--canvas-warm)' : 'var(--canvas-card)',
            cursor: 'pointer', fontSize: 13, fontWeight: 500, transition: 'all 0.15s'
          }}>
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} style={{ display: 'none' }} />
            {selected.includes(opt) && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="3">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
            {opt}
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Score Ring ────────────────────────────────────────────────
export function ScoreRing({ score, size = 80 }) {
  const r = (size / 2) - 8;
  const circ = 2 * Math.PI * r;
  const fill = score ? (score / 100) * circ : 0;
  const color = score >= 70 ? 'var(--sage)' : score >= 45 ? 'var(--gold)' : 'var(--accent)';

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={6} />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={color} strokeWidth={6}
        strokeDasharray={`${fill} ${circ}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ animation: 'scoreIn 1s ease both', transition: 'stroke-dasharray 0.8s ease' }}
      />
      <text x={size / 2} y={size / 2 + 1} textAnchor="middle" dominantBaseline="middle"
        fontSize={size * 0.22} fontWeight="700" fill="var(--ink)" fontFamily="var(--font-body)">
        {score != null ? score : '–'}
      </text>
    </svg>
  );
}

// ── Progress Bar ──────────────────────────────────────────────
export function ProgressBar({ value, color = 'var(--sage)' }) {
  return (
    <div className="progress-bar">
      <div className="progress-fill" style={{ width: `${Math.min(value || 0, 100)}%`, background: color }} />
    </div>
  );
}

// ── Toast Renderer ────────────────────────────────────────────
export function ToastContainer({ toasts }) {
  return (
    <div className="toast-container">
      {toasts.map(t => (
        <div key={t.id} className={`toast ${t.type}`}>
                    {t.message}
        </div>
      ))}
    </div>
  );
}

// ── File Upload ───────────────────────────────────────────────
export function FileUpload({ label, accept, onFile, file, hint }) {
  const ref = useRef();
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div
        onClick={() => ref.current?.click()}
        style={{
          border: '2px dashed var(--border-strong)', borderRadius: 'var(--radius-md)',
          padding: '28px', textAlign: 'center', cursor: 'pointer',
          background: file ? 'var(--sage-pale)' : 'var(--canvas-warm)',
          transition: 'all 0.2s'
        }}
      >
        <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
          onChange={e => e.target.files[0] && onFile(e.target.files[0])} />
        {file ? (
          <div style={{ color: 'var(--sage)' }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{file.name}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 4 }}>Click to replace</div>
          </div>
        ) : (
          <div style={{ color: 'var(--ink-muted)' }}>
            <div style={{ fontWeight: 500, fontSize: 14 }}>Click to upload</div>
            {hint && <div style={{ fontSize: 12, marginTop: 4 }}>{hint}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────
export function Avatar({ name = '', size = 44 }) {
  const initials = name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const hue = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `hsl(${hue}, 35%, 75%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-display)', fontSize: size * 0.38,
      color: `hsl(${hue}, 35%, 25%)`, flexShrink: 0, fontWeight: 400
    }}>
      {initials || '?'}
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────
export function EmptyState({ icon, title, message, action }) {
  return (
    <div className="empty-state">
      {icon && <div style={{ fontSize: 48, marginBottom: 12 }}>{icon}</div>}
      <h3>{title}</h3>
      <p>{message}</p>
      {action}
    </div>
  );
}

// ── Section Header ────────────────────────────────────────────
export function SectionHeader({ title, sub, action }) {
  return (
    <div className="section-header">
      <div>
        <h2 className="section-title">{title}</h2>
        {sub && <p className="section-sub">{sub}</p>}
      </div>
      {action}
    </div>
  );
}
